export const getCssVar = (name, fallback) => {
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return val || fallback;
};

// Mermaid 10's color parser only accepts hex/rgb()/named colors; it rejects
// oklch() and other modern CSS color syntax. The design tokens use oklch, so
// resolve any oklch() value to an rgba() string before handing it to Mermaid.
// This is the integration boundary the Decision Record calls out (Decision 7:
// Mermaid is a compatibility surface).
//
// Chromium preserves the author-specified color format from getComputedStyle
// and canvas fillStyle (oklch stays oklch), so we parse it ourselves.
const oklchToRgba = (L, C, H, a = 1) => {
  const hr = (H * Math.PI) / 180;
  const a_ = Math.cos(hr) * C;
  const b_ = Math.sin(hr) * C;
  const l_ = L + 0.3963377774 * a_ + 0.2158037573 * b_;
  const m_ = L - 0.1055613458 * a_ - 0.0638541728 * b_;
  const s_ = L - 0.0894841775 * a_ - 1.2914855480 * b_;
  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574051 * m - 0.3413193965 * s;
  const b = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s;
  const lin = (c) => (c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055);
  const cl = (c) => Math.max(0, Math.min(1, lin(c)));
  const R = Math.round(cl(r) * 255);
  const G = Math.round(cl(g) * 255);
  const B = Math.round(cl(b) * 255);
  return a >= 1 ? `rgb(${R}, ${G}, ${B})` : `rgba(${R}, ${G}, ${B}, ${Math.round(a * 1000) / 1000})`;
};

const resolveColor = (value) => {
  if (!value) return value;
  const v = value.trim();
  // Fast path: hex, rgb()/rgba(), hsl(), named colors are accepted by Mermaid.
  if (/^(#|rgb|hsl|transparent|currentColor)/i.test(v)) return value;
  const m = v.match(/^oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+)%?)?\s*\)$/i);
  if (m) {
    const L = parseFloat(m[1]);
    const C = parseFloat(m[2]);
    const H = parseFloat(m[3]);
    const a = m[4] ? parseFloat(m[4]) / (m[4].endsWith('%') ? 100 : 1) : 1;
    return oklchToRgba(L, C, H, a);
  }
  return value;
};

export const getColorVar = (name, fallback) => resolveColor(getCssVar(name, fallback));

export const getMermaidConfig = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return {
    startOnLoad: true,
    theme: isDark ? 'dark' : 'base',
    fontFamily: getCssVar('--font-body', '"Inter", "Open Sans", system-ui, sans-serif'),
    themeVariables: {
      primaryColor: getColorVar('--color-background-secondary', '#faf9f7'),
      primaryTextColor: getColorVar('--color-font', '#2a2d34'),
      primaryBorderColor: getColorVar('--color-accent', isDark ? '#a5b4fc' : '#0070ff'),
      lineColor: getColorVar('--color-accent', isDark ? '#a5b4fc' : '#0070ff'),
      secondaryColor: getColorVar('--color-background-secondary', '#faf9f7'),
      tertiaryColor: getColorVar('--color-background-primary', '#ffffff'),
    },
  };
};

export const reinitMermaid = () => {
  if (typeof window !== 'undefined' && window.mermaid) {
    window.mermaid.initialize(getMermaidConfig());

    // Clear existing SVGs and restore original definitions to force re-render
    document.querySelectorAll('.mermaid').forEach((el) => {
      const definition = el.dataset.graphDefinition;
      if (definition) {
        el.innerHTML = '';
        el.textContent = definition;
        el.removeAttribute('data-processed');
      }
    });

    window.mermaid.init(undefined, '.mermaid');

    document.querySelectorAll('.mermaid').forEach((el) => {
      el.setAttribute('role', 'img');
      if (!el.getAttribute('aria-label')) {
        el.setAttribute('aria-label', 'Mermaid diagram');
      }
    });
  }
};
